import {inject, Injectable} from '@angular/core';
import * as amplitude from '@amplitude/analytics-browser';

import {environment} from "../../../environments/environment";
import {NavigationEnd, Router} from "@angular/router";
import {UserService} from "./user.service";
import {distinctUntilChanged, filter, map, merge, take} from "rxjs";
import {BotDetectionService} from "./bot-detection.service";
import {PlatformService} from "./platform.service";
import {platform} from "../utils";
import {AnalyticsService, AmplitudeEvent, PaymentEventProperties, StripeCheckoutReturn} from "./analytics.service";
import {PixelEventName, PixelEventParams, TikTokTrackingService} from "./tiktok-tracking.service";
import {isNative} from "../utils";
import {v4 as uuidv4} from 'uuid';
import {claimOnce} from "./claim-once";
import {TrackingPixelService} from "./tracking-pixel.service";
import {urlScrubPlugin} from "./amplitude-url-scrub.plugin";

const isBrowserEnvironment =
  typeof document !== 'undefined' &&
  typeof window !== 'undefined';
const amplitudeReturn: amplitude.Types.AmplitudeReturn<void> | null = isBrowserEnvironment
  ? amplitude.init(environment.amplitudeKey, {
      serverUrl: environment.amplitudeServerUrl,
      autocapture: {
        elementInteractions: false,
        pageViews: true,
        sessions: true,
        attribution: true,
        formInteractions: false,
        fileDownloads: false,
      },
      logLevel: amplitude.Types.LogLevel.Warn,
      transport: 'beacon',
    })
  : null;

const platformMap: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
};

const mappedPlatform = platformMap[platform] ?? 'Unknown';

const googleAdsConversionLabels = {
  signup: 'vNWNCKKmgvcbEJmckfQ-',
  purchase: 'gzAlCKL5gfcbEJmckfQ-',
} as const;

// A proxy value for a sign-up, not a price: Google Ads bids on conversion value.
const googleAdsSignupValue = 2;

// Checkout sessions whose conversion this browser already sent, so a reload, Back or a re-typed
// success URL doesn't count it again. claimFirstVisit also stops Back and reload where it's used;
// this covers the rest and a blocked history state. A short list: a session only matters while
// its success page can be revisited.
const claimConversion = (sessionId: string) => claimOnce('homey-pixel-conversions', sessionId, 20);

let nativeAppVersion: string | undefined;

const platformPlugin: amplitude.Types.EnrichmentPlugin = {
  name: 'PlatformPlugin',
  type: 'enrichment',
  setup: async () => {
    if (isNative) {
      try {
        const {App} = await import('@capacitor/app');
        const info = await App.getInfo();
        nativeAppVersion = info.version;
      } catch (_) {}
    }
  },

  execute: async (event) => {
    event.platform = mappedPlatform;
    if (nativeAppVersion) {
      event.app_version = nativeAppVersion;
    }
    return event;
  },
};

if (isBrowserEnvironment) {
  amplitude.add(platformPlugin);
  amplitude.add(urlScrubPlugin);
}


@Injectable({
  providedIn: 'root'
})
export class BrowserAnalyticsService extends AnalyticsService {
  private readonly platformService = inject(PlatformService);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly botDetectionService = inject(BotDetectionService);
  private readonly tikTokTrackingService = inject(TikTokTrackingService);
  private readonly trackingPixelService = inject(TrackingPixelService);

  public async sendEvent(event: AmplitudeEvent, properties?: Record<string, string | number | undefined>) {
    if (this.platformService.isServer || !amplitudeReturn) {
      return;
    }

    // Fire pixel events immediately — do NOT gate them behind Amplitude awaits,
    // otherwise page navigations can prevent them from executing.
    const pixelEvent = this.mapToPixelEventName(event);
    if (pixelEvent) {
      const amount = properties?.['amount'];
      this.trackPixelEvent(pixelEvent, {
        eventId: uuidv4(),
        value: pixelEvent === 'InitiateCheckout' && typeof amount === 'number' ? amount : undefined,
      });
      if (pixelEvent === 'CompleteRegistration') {
        this.trackGoogleAdsConversion(googleAdsConversionLabels.signup, googleAdsSignupValue, undefined);
      }
    }

    try {
      await amplitudeReturn.promise;
      await amplitude.track(`[Homey] ${event}`, properties).promise;
      await amplitude.flush().promise;
    } catch (e) {
      console.error('Failed to send event', e);
    }

  }

  // payment-success is not here: sendPaymentSuccess forwards it, once per Checkout session.
  private mapToPixelEventName(event: AmplitudeEvent): PixelEventName | null {
    switch (event) {
      case 'signup-success':
        return 'CompleteRegistration';
      case 'payment-started':
      case 'resubscribe-payment-started':
        return 'InitiateCheckout';
      case 'benefits-page-viewed':
        return 'ViewContent';
      default:
        return null;
    }
  }

  private pixelsEnabled(): boolean {
    return this.trackingPixelService.pixelsActive();
  }

  /** Sends one standard event to Meta and TikTok. */
  private trackPixelEvent(name: PixelEventName, params: PixelEventParams) {
    if (this.platformService.isServer || !this.pixelsEnabled()) {
      return;
    }
    this.trackFacebookEvent(name, params);
    this.tikTokTrackingService.trackEvent(name, params);
  }

  public async setClickSource(source: string, url: string, variant?: string) {
    if (this.platformService.isServer || !amplitudeReturn) {
      return;
    }

    try {
      const eventProps: Record<string, string | undefined> = {
        click_source: source,
        url: url,
        click_variant: variant,
      };

      this.sendEvent('click-tracked', eventProps);

      // Flushed rather than left to batch: a property that lands after the events it was meant
      // to segment is a property those events do not carry.
      await amplitudeReturn.promise;
      const identify = new amplitude.Identify();
      identify.set('click_source', source);
      if (variant !== undefined) {
        identify.set('click_variant', variant);
      }
      await amplitude.identify(identify).promise;
      await amplitude.flush().promise;
    } catch (e) {
      console.error('Failed to set click campaign', e);
    }
  }

  private trackGoogleAdsConversion(label: string, value: number | undefined, transactionId: string | undefined) {
    if (this.platformService.isServer || !this.pixelsEnabled() || !environment.googleAdsId) {
      return;
    }

    const gtag: unknown = Reflect.get(window, 'gtag');
    if (typeof gtag !== 'function') {
      return;
    }

    try {
      gtag('event', 'conversion', {
        send_to: `${environment.googleAdsId}/${label}`,
        ...(value === undefined ? {} : {value, currency: 'USD'}),
        ...(transactionId === undefined ? {} : {transaction_id: transactionId}),
      });

    } catch (err) {
      console.warn('Google Ads track error', err);
    }
  }

  public buildPaymentEventProperties(
    base: {
      flow: PaymentEventProperties['flow'];
      checkoutLocation: PaymentEventProperties['checkoutLocation'];
      interval: PaymentEventProperties['interval'];
    }
  ): PaymentEventProperties {
    return {
      ...base,
      interval: base.interval ?? 'unknown',
    };
  }

  public async sendPaymentStarted(params: {
    flow: 'initial' | 'resubscribe',
    interval: 'monthly' | 'annual',
    checkoutLocation: 'default' | 'deferred',
    amount: number,
    couponId?: string,
  }) {
    try {
      const event = params.flow === 'resubscribe' ? 'resubscribe-payment-started' : 'payment-started';
      const properties = this.buildPaymentEventProperties({
        flow: params.flow,
        interval: params.interval,
        checkoutLocation: params.checkoutLocation,
      });

      await this.sendEvent(event, {...properties, amount: params.amount, couponId: params.couponId});
    } catch (err) {
      console.error('sendPaymentStarted analytics error', err);
    }
  }

  public async sendPaymentSuccess(params: {
    flow: 'initial' | 'resubscribe',
    checkoutLocation: 'default' | 'deferred',
    interval: PaymentEventProperties['interval'],
    checkout: StripeCheckoutReturn | undefined,
  }) {
    try {
      const {checkout} = params;
      // Only a real Stripe return counts as a conversion for the ads, once per session. Amplitude
      // gets every call, RevenueCat purchases included (the pages' claimFirstVisit decides those).
      if (checkout && this.pixelsEnabled() && claimConversion(checkout.sessionId)) {
        // Purchase stays at trial start, as the campaigns optimise on it: the first charge comes
        // after Meta's 7-day click window.
        this.trackPixelEvent('Purchase', {eventId: checkout.sessionId, value: checkout.value});
        if (checkout.trial) {
          this.trackPixelEvent('StartTrial', {eventId: `${checkout.sessionId}-trial`, value: checkout.value});
        }
        this.trackGoogleAdsConversion(googleAdsConversionLabels.purchase, checkout.value, checkout.sessionId);
      }

      const properties = this.buildPaymentEventProperties({
        flow: params.flow,
        checkoutLocation: params.checkoutLocation,
        interval: params.interval,
      });
      await this.sendEvent('payment-success', {...properties, amount: checkout?.value});
    } catch (err) {
      console.error('sendPaymentSuccess analytics error', err);
    }
  }

  public getUTMData(): {utmSource: string, utmCampaign: string} {
    const prefix = environment.amplitudeKey.slice(0, 10);
    const cookieName = `AMP_MKTG_${prefix}`;
    let utmSource = 'unknown';
    let utmCampaign = 'unknown';
    if (!isBrowserEnvironment) {
      return {utmSource, utmCampaign};
    }

    try {
      // grab the cookie value
      const raw = document.cookie
        .split('; ')
        .find((c) => c.startsWith(`${cookieName}=`))
        ?.split('=')[1];

      if (raw) {
        // parse JSON safely
        const uriEncoded = atob(raw);                         // :contentReference[oaicite:1]{index=1}
        const data = JSON.parse(decodeURIComponent(uriEncoded));
        // pick out only what you need, falling back to defaults
        utmSource   = data.utm_source   ?? utmSource;
        utmCampaign = data.utm_campaign ?? utmCampaign;
      }
    } catch (err) {
      // swallow any errors—defaults will stay in place
      console.error('getUTMData error:', err);
    }

    return { utmSource, utmCampaign };
  }

  public initialize() {
    if (this.platformService.isServer || !amplitudeReturn) {
      return;
    }

    this.tikTokTrackingService.initialize();

    try {
      amplitudeReturn.promise.then(() => {
          this.userService.user$.pipe(
            map(user => user.status !== 'unauthorized' ? user.id : undefined),
            filter(id => !!id),
            distinctUntilChanged(),
          ).subscribe(id => {
            amplitude.setUserId(id);
          });

          amplitude.flush();

          this.setupSessionRecording();
        }
      )
    } catch (e) {
      console.error('Failed to initialize Amplitude', e);
    }
  }

  async setupSessionRecording() {
    if (environment.amplitude.sessionRecordingEnabled) {
      try {
        const bot = await this.botDetectionService.isBot();

        if (bot) {
          return;
        }

        const relevantPage = this.router.events.pipe(
          filter(event => event instanceof NavigationEnd),
          filter(event => event.url.includes('signup') || event.url.includes('login')),
        );

        const loggedInUser = this.userService.user$.pipe(
          filter(user => user.status !== 'unauthorized'),
        );

        merge(relevantPage, loggedInUser).pipe(
          take(1),
        ).subscribe(async () => {
          const { sessionReplayPlugin } = await import('@amplitude/plugin-session-replay-browser');
          amplitude.add(
            sessionReplayPlugin({
              sampleRate: 0.25,
            }),
          );
        });

      } catch (e) {
        console.error('Failed to setup session recording', e);
      }
    }
  }

  private trackFacebookEvent(name: PixelEventName, params: PixelEventParams) {
    const fbq: unknown = Reflect.get(window, 'fbq');
    if (typeof fbq !== 'function') {
      return;
    }

    try {
      const data = params.value === undefined ? {} : {value: params.value, currency: 'USD'};
      fbq('track', name, data, {eventID: params.eventId});
    } catch (err) {
      console.error('Facebook pixel track error', err);
    }
  }
}
